import { useState, useEffect } from "react";
import { useTranslation } from "../../../components/Translation";
import { gradStudents, GradStudent } from "../../../constants/ourTeam/students";
import { localizeName } from "../../../utils/localizeName";

function StudentModal({ student: s, onClose }: { student: GradStudent; onClose: () => void }) {
  const { t, language } = useTranslation();
  const [photoLeft] = useState(() => Math.random() > 0.5);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const levelColor = s.level === "doctorado" ? "bg-indigo-600" : "bg-teal-600";

  const PhotoBlock = (
    <div className="relative w-full md:w-2/5 flex-shrink-0 min-h-64 md:min-h-full bg-gray-100">
      <img src={s.image} alt={s.name} className="w-full h-full object-cover object-top" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
      <span className={`absolute top-3 left-3 text-micro text-white font-bold px-2 py-0.5 rounded ${levelColor}`}>
        {t(`researchers.${s.roleKey}`)}
      </span>
      <div className="absolute bottom-4 left-4 right-4">
        <h3 className="text-white text-lg font-bold leading-tight">{localizeName(s.name, language)}</h3>
      </div>
    </div>
  );

  const TextBlock = (
    <div className="flex-1 p-6 overflow-y-auto space-y-4">
      {/* Semblanza */}
      <p className="text-mini text-gray-600 leading-relaxed text-justify">
        {t(`researchers.${s.descriptionKey}`)}
      </p>

      {/* Tesis */}
      {s.thesisTitleKey && (
        <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-dorado-uady">
          <p className="text-micro font-bold text-azul-uady uppercase tracking-wider mb-1">
            {t('researchers.student_tesis')}
          </p>
          <p className="text-mini font-semibold text-azul-uady mb-2">
            {t(`researchers.${s.thesisTitleKey}`)}
          </p>
          {s.thesisDescKey && (
            <p className="text-micro text-gray-500 leading-relaxed text-justify">
              {t(`researchers.${s.thesisDescKey}`)}
            </p>
          )}
        </div>
      )}

      {/* Año de titulación */}
      {s.graduationYear && (
        <div className="flex items-center gap-2">
          <span className="text-micro font-bold text-azul-uady uppercase tracking-wider">
            {t('researchers.student_graduacion')}:
          </span>
          <span className="text-mini text-dorado-uady font-semibold">{s.graduationYear}</span>
        </div>
      )}

      {/* Especialidades */}
      {s.specialtyKeys && s.specialtyKeys.length > 0 && (
        <div>
          <p className="text-micro font-bold text-azul-uady uppercase tracking-wider mb-2">
            {t('researchers.investigador_especialidades')}
          </p>
          <div className="flex flex-wrap gap-2">
            {s.specialtyKeys.map((key, i) => (
              <span key={i} className="text-micro bg-azul-uady/10 text-azul-uady font-semibold px-3 py-1 rounded-full">
                {t(`researchers.${key}`)}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Contacto */}
      {s.email && (
        <div>
          <p className="text-micro font-bold text-azul-uady uppercase tracking-wider mb-1">
            {t('researchers.investigador_contacto')}
          </p>
          <a href={`mailto:${s.email}`} className="text-mini text-dorado-uady hover:underline">
            {s.email}
          </a>
        </div>
      )}

      {/* Footer botones */}
      <div className="flex items-center justify-between pt-2 gap-3">
        <div className="flex items-center gap-2">
          {s.linkedin && (
            <a href={s.linkedin} target="_blank" rel="noopener noreferrer"
              className="w-9 h-9 flex items-center justify-center rounded-full bg-[#0077b5]/10 hover:bg-[#0077b5] group transition-colors"
              title="LinkedIn"
            >
              <svg className="w-4 h-4 text-[#0077b5] group-hover:text-white transition-colors" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
          )}
        </div>
        <button
          onClick={onClose}
          className="border border-gray-200 text-gray-500 text-mini font-semibold px-5 py-2 rounded hover:border-gray-400 transition-colors"
        >
          {t('researchers.student_cerrar')}
        </button>
      </div>
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col md:flex-row max-h-[90vh] relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center bg-black/40 hover:bg-black/60 rounded-full text-white transition-colors"
        >
          ✕
        </button>
        {photoLeft ? <>{PhotoBlock}{TextBlock}</> : <>{TextBlock}{PhotoBlock}</>}
      </div>
    </div>
  );
}

function StudentCard({ student: s, onClick }: { student: GradStudent; onClick: () => void }) {
  const { t, language } = useTranslation();
  const levelColor = s.level === "doctorado" ? "bg-indigo-600" : "bg-teal-600";

  return (
    <div
      onClick={onClick}
      className="group bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md hover:scale-[1.02] transition-all duration-300 flex flex-col cursor-pointer w-64"
    >
      <div className="relative h-52 bg-gray-100 flex-shrink-0">
        <img
          src={s.image}
          alt={s.name}
          className="w-full h-full object-cover object-top grayscale group-hover:grayscale-0 transition-all duration-500"
        />
        <span className={`absolute top-2 right-2 text-white text-micro font-bold px-2 py-0.5 rounded ${levelColor}`}>
          {t(`researchers.${s.roleKey}`)}
        </span>
      </div>
      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-bold text-azul-uady text-mini mb-1">{localizeName(s.name, language)}</h3>
        {s.thesisTitleKey && (
          <p className="text-micro text-dorado-uady font-semibold line-clamp-1 mb-2">
            {t(`researchers.${s.thesisTitleKey}`)}
          </p>
        )}
        <p className="text-micro text-gray-500 leading-relaxed line-clamp-3 flex-1 text-justify">
          {t(`researchers.${s.descriptionKey}`)}
        </p>
        {s.graduationYear && (
          <p className="text-micro text-gray-400 mt-3">
            🎓 {t('researchers.student_graduacion')}: <span className="font-semibold text-azul-uady">{s.graduationYear}</span>
          </p>
        )}
      </div>
    </div>
  );
}

export default function GradStudents() {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<GradStudent | null>(null);

  const docStudents = gradStudents.filter(s => s.level === "doctorado");
  const maeStudents = gradStudents.filter(s => s.level === "maestria");

  return (
    <div className="py-14 px-6 max-w-6xl mx-auto">
      {/* Título sección */}
      <div className="flex items-center gap-2 mb-2">
        <div className="w-1 h-6 bg-dorado-uady rounded" />
        <h2 className="text-2xl font-bold text-azul-uady">
          {t('researchers.posgrado_titulo')}
        </h2>
      </div>
      <p className="text-gray-400 text-mini mb-10">
        {t('researchers.posgrado_subtitulo')}
      </p>

      {/* Doctorado */}
      {docStudents.length > 0 && (
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-5">
            <span className="bg-indigo-600 text-azul-uady text-xl font-bold px-3 py-1 rounded-full">
              {t('researchers.section_doctorado')}
            </span>
          </div>
          <div className="flex flex-wrap justify-center gap-6">
            {docStudents.map((s, i) => (
              <StudentCard key={i} student={s} onClick={() => setSelected(s)} />
            ))}
          </div>
        </div>
      )}

      {/* Maestría */}
      {maeStudents.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-5">
            <span className="bg-teal-600 text-azul-uady text-xl font-bold px-3 py-1 rounded-full">
              {t('researchers.section_maestria')}
            </span>
          </div>
          <div className="flex flex-wrap justify-center gap-6">
            {maeStudents.map((s, i) => (
              <StudentCard key={i} student={s} onClick={() => setSelected(s)} />
            ))}
          </div>
        </div>
      )}

      {selected && (
        <StudentModal student={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}